import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  staffAddressTypeListApi,
  staffAddressTypeCreateApi,
  staffAddressTypeUpdateApi,
  staffAddressTypeDeleteApi,
} from "../../../../helpers/fakebackend_helper";

const DEFAULT_BG = "#EEF2FF";
const DEFAULT_TEXT = "#4F46E5";

const EMPTY = {
  Name: "",
  Description: "",
  CssColourBackground: DEFAULT_BG,
  CssColourText: DEFAULT_TEXT,
  Order: "",
  FlagActive: 1,
  FlagDefaultUseForShiftPlanner: 0,
};

/**
 * Task 24860 — the customisable labels a staff address can carry
 * ("Home", "Temporary Address", "Previous Address", …).
 *
 * Calls the API directly rather than going through a redux slice. The sibling
 * Contact Types tab has one, but nothing outside these two screens needs the
 * address types in store, and the Addresses tab on the staff profile already
 * reads them the same way — keeping one feature on one pattern beats matching
 * the tab next door.
 */
export default function StaffAddressTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await staffAddressTypeListApi();
      if (response?.status === 200) {
        setTypes(response.staffaddresstype_list || []);
      } else {
        toast.error(response?.message || "Could not load the address types");
      }
    } catch (e) {
      toast.error("Could not load the address types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.Name.trim()) {
      toast.error("Give the label a name");
      return;
    }
    const row = {
      Name: form.Name.trim(),
      Description: form.Description,
      CssColourBackground: form.CssColourBackground,
      CssColourText: form.CssColourText,
      Order: form.Order === "" ? null : Number(form.Order),
      FlagActive: form.FlagActive ? 1 : 0,
      FlagDefaultUseForShiftPlanner: form.FlagDefaultUseForShiftPlanner ? 1 : 0,
    };

    setSaving(true);
    try {
      const response = editingId
        ? await staffAddressTypeUpdateApi([{ Id: editingId, ...row }])
        : await staffAddressTypeCreateApi([row]);

      if (response?.status === 200) {
        toast.success(
          response.message ||
            (editingId ? "Address type updated" : "Address type added")
        );
        reset();
        load();
      } else {
        // The delete endpoint refuses a label still in use and says how many
        // addresses hold it — surface that rather than a generic failure.
        toast.error(response?.message || "Could not save that address type");
      }
    } catch (e) {
      toast.error("Could not save that address type");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await staffAddressTypeDeleteApi([deleteId]);
      if (response?.status === 200) {
        toast.success(response.message || "Address type deleted");
        load();
      } else {
        toast.error(response?.message || "Could not delete that address type");
      }
    } catch (e) {
      toast.error("Could not delete that address type");
    } finally {
      setDeleteId(null);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.Id);
    setForm({
      Name: t.Name || "",
      Description: t.Description || "",
      CssColourBackground: t.CssColourBackground || DEFAULT_BG,
      CssColourText: t.CssColourText || DEFAULT_TEXT,
      Order: t.Order ?? "",
      FlagActive: String(t.FlagActive) === "0" ? 0 : 1,
      FlagDefaultUseForShiftPlanner:
        String(t.FlagDefaultUseForShiftPlanner) === "1" ? 1 : 0,
    });
  };

  return (
    <>
      <DeleteModal
        show={!!deleteId}
        onCloseClick={() => setDeleteId(null)}
        onDeleteClick={handleDelete}
      />

      <InfoCard
        title={editingId ? "Edit address type" : "Add an address type"}
        icon="ri-price-tag-3-line"
        accent="indigo"
      >
        <Row className="g-3 align-items-end">
          <Col lg={3}>
            <Label className="mb-1">Name</Label>
            <Input
              value={form.Name}
              onChange={(e) => setForm({ ...form, Name: e.target.value })}
              placeholder="e.g. Temporary Address"
            />
          </Col>
          <Col lg={4}>
            <Label className="mb-1">Description</Label>
            <Input
              value={form.Description}
              onChange={(e) => setForm({ ...form, Description: e.target.value })}
              placeholder="What this label means"
            />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Order</Label>
            <Input
              type="number"
              value={form.Order}
              onChange={(e) => setForm({ ...form, Order: e.target.value })}
            />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">Chip colour</Label>
            <div className="d-flex align-items-center gap-2">
              <span
                className="badge"
                style={{
                  background: form.CssColourBackground,
                  color: form.CssColourText,
                }}
              >
                {form.Name.trim() || "Label"}
              </span>
              <input
                type="color"
                aria-label="Background colour"
                value={form.CssColourBackground}
                onChange={(e) =>
                  setForm({ ...form, CssColourBackground: e.target.value })
                }
              />
              <input
                type="color"
                aria-label="Text colour"
                value={form.CssColourText}
                onChange={(e) =>
                  setForm({ ...form, CssColourText: e.target.value })
                }
              />
            </div>
          </Col>

          <Col lg={12}>
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="addressTypePlanner"
                checked={!!form.FlagDefaultUseForShiftPlanner}
                onChange={(e) =>
                  setForm({
                    ...form,
                    FlagDefaultUseForShiftPlanner: e.target.checked ? 1 : 0,
                  })
                }
              />
              <Label className="form-check-label" for="addressTypePlanner">
                Addresses of this type are used by the Shift Planner by default
                — tick it for the kinds of address somebody actually lives at,
                and nobody has to remember the box on every address they enter.
                Any individual address can still be changed afterwards.
              </Label>
            </div>
          </Col>

          <Col lg={12}>
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="addressTypeActive"
                checked={!!form.FlagActive}
                onChange={(e) =>
                  setForm({ ...form, FlagActive: e.target.checked ? 1 : 0 })
                }
              />
              <Label className="form-check-label" for="addressTypeActive">
                Active — inactive labels stay on existing addresses but are not
                offered for new ones
              </Label>
            </div>
          </Col>

          <Col lg={12}>
            <div className="d-flex gap-2">
              <Button color="success" disabled={saving} onClick={handleSave}>
                <i className="ri-save-line align-bottom me-1" />
                {editingId ? "Save changes" : "Add address type"}
              </Button>
              {editingId && (
                <Button color="light" onClick={reset}>
                  Cancel
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </InfoCard>

      <InfoCard title="Address types" icon="ri-list-check" accent="indigo">
        {loading ? (
          <LoadingSpinner />
        ) : isEmpty(types) ? (
          <p className="text-muted mb-0">No address types yet.</p>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-wrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Label</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Shift Planner</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.Id}>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: t.CssColourBackground || DEFAULT_BG,
                          color: t.CssColourText || DEFAULT_TEXT,
                        }}
                      >
                        {t.Name}
                      </span>
                    </td>
                    <td className="text-muted">{t.Description || "—"}</td>
                    <td className="text-muted">{t.Order ?? "—"}</td>
                    <td>
                      {String(t.FlagDefaultUseForShiftPlanner) === "1" ? (
                        <span className="badge bg-primary-subtle text-primary">
                          <i className="ri-map-pin-user-line align-bottom me-1" />
                          Default
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {String(t.FlagActive) === "0" ? (
                        <span className="badge bg-secondary-subtle text-secondary">
                          Inactive
                        </span>
                      ) : (
                        <span className="badge bg-success-subtle text-success">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        title="Edit"
                        onClick={() => startEdit(t)}
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Delete"
                        onClick={() => setDeleteId(t.Id)}
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </InfoCard>
    </>
  );
}
